import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, type LucideIcon } from 'lucide-react'
import { BottomSheet, useBottomSheet } from '@/components/ui/bottom-sheet'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { FilterMenu, FilterOptionList } from './filter-menu'

export interface FilterOption<V extends string = string> {
  value: V
  label: string
  icon?: LucideIcon
  count?: number | null
  /** A short green tag after the label ("−17%"), the checkout's savings badge. */
  badge?: string | null
  /** A second line in the menu (never on a pill). */
  description?: string
  /** The pill shows only its icon; the label stays for screen readers and the tooltip. */
  hideLabel?: boolean
}

export interface FilterPillsProps<V extends string = string> {
  /** The first option is the default ("All"). */
  options: FilterOption<V>[]
  value: V
  onChange: (value: V) => void
  'aria-label': string
  /** Above this many options the pills become a `FilterMenu` (default 7). */
  max?: number
  /** The menu's name when the pills collapse into one (defaults to the aria-label). */
  menuLabel?: string
  /**
   * `tabs` for a view (`ViewSwitch`): a tab list with `aria-selected`, one tab
   * stop and arrow keys. Default `filter`: toggle buttons (`aria-pressed`).
   */
  semantics?: 'filter' | 'tabs'
  className?: string
  'data-testid'?: string
}

/** The Feed pill (F-181), shared: the visual of `Feed.tsx`, unchanged. */
export const filterPillClass = (active: boolean) =>
  cn(
    'relative flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
    /* A 40 px touch target on a phone with the same look: the hit area grows, the pill does not. */
    "after:absolute after:inset-x-0 after:-inset-y-1 after:content-[''] sm:after:hidden",
    active ? 'bg-primary text-white' : 'bg-white text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-900'
  )

const GAP = 8
/* The row's own padding (`px-1`, both sides): room for the focus ring, which `overflow-hidden` would cut. */
const PAD_X = 8

export const usePhone = () => {
  const [phone, setPhone] = React.useState(() => typeof window !== 'undefined' && !window.matchMedia('(min-width: 640px)').matches)
  React.useEffect(() => {
    const media = window.matchMedia('(min-width: 640px)')
    const onChange = () => setPhone(!media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return phone
}

/**
 * Which options fit in one line ("priority+"): the chosen one first, then the
 * others in order; what is left goes to "More". All of them when they fit.
 */
const fitting = (widths: number[], moreWidth: number, chosenMoreWidth: number, chosen: number, room: number): Set<number> => {
  const all = widths.reduce((sum, w) => sum + w, 0) + GAP * Math.max(0, widths.length - 1)
  /* 1 px of slack: a row sized by its own content (a view in a one-line toolbar) rounds its width down. */
  if (all <= room + 1) return new Set(widths.map((_, i) => i))
  const shown = new Set<number>()
  let used = moreWidth
  const tryAdd = (i: number) => {
    if (i < 0 || shown.has(i) || used + GAP + widths[i] > room) return false
    shown.add(i)
    used += GAP + widths[i]
    return true
  }
  /* The chosen one stays on the line; if not even it fits, "More" shows its name instead. */
  if (!tryAdd(chosen) && chosen >= 0) used = chosenMoreWidth
  for (let i = 0; i < widths.length; i++) {
    if (!shown.has(i) && !tryAdd(i)) break
  }
  return shown
}

/**
 * The quick filter of a list (F-182, layer 3): the main cut, up to 7 values,
 * one row per screen. Always one line, never scrolls sideways: the pills that
 * do not fit the container go into a last "More ▾" pill (a menu; a bottom
 * sheet on a phone), which shows the chosen value's name when it is in there.
 * More than `max` values become a `FilterMenu` with the same options.
 */
export function FilterPills<V extends string = string>({ options, value, onChange, max = 7, menuLabel, semantics = 'filter', className, ...rest }: FilterPillsProps<V>) {
  const tabs = semantics === 'tabs'
  const { t } = useTranslation('filters')
  const phone = usePhone()
  const sheet = useBottomSheet()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const rowRef = React.useRef<HTMLDivElement>(null)
  const measureRef = React.useRef<HTMLDivElement>(null)
  const [shown, setShown] = React.useState<Set<number> | null>(null)

  const chosen = options.findIndex((o) => o.value === value)
  const key = options.map((o) => `${o.value}:${o.label}:${o.count ?? ''}:${o.badge ?? ''}`).join('|')

  React.useLayoutEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure) return
    const compute = () => {
      const items = Array.from(measure.children) as HTMLElement[]
      const widths = items.slice(0, options.length).map((el) => el.getBoundingClientRect().width)
      const moreWidth = items[options.length]?.getBoundingClientRect().width ?? 0
      /* "More" carrying the chosen name: its pill plus the chevron. */
      const chosenMoreWidth = chosen >= 0 ? widths[chosen] + 20 : moreWidth
      const next = fitting(widths, moreWidth, chosenMoreWidth, chosen, row.clientWidth - PAD_X)
      setShown((prev) => (prev && prev.size === next.size && [...next].every((i) => prev.has(i)) ? prev : next))
    }
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(row)
    /* Web fonts change the widths once they load. */
    void document.fonts?.ready.then(compute)
    return () => observer.disconnect()
  }, [key, chosen, options.length])

  /* Arrow keys (tabs): the next option is chosen, then focused once it is on the line (or "More" carries it). */
  const focusNext = React.useRef(false)
  React.useEffect(() => {
    if (!focusNext.current) return
    focusNext.current = false
    const row = rowRef.current
    const target = row?.querySelector<HTMLElement>(`[role="tab"][aria-selected="true"]`) ?? row?.querySelector<HTMLElement>('[data-testid="filter-pills-more"]')
    target?.focus()
  }, [value, shown])

  const onTabKeyDown = (event: React.KeyboardEvent) => {
    if (!tabs || chosen < 0) return
    const last = options.length - 1
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? (chosen === last ? 0 : chosen + 1)
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? (chosen === 0 ? last : chosen - 1)
          : event.key === 'Home' ? 0
            : event.key === 'End' ? last
              : null
    if (next === null || (event.target as HTMLElement).closest('[role="dialog"]')) return
    event.preventDefault()
    focusNext.current = true
    onChange(options[next].value)
  }

  if (options.length > max) {
    return (
      <FilterMenu
        label={menuLabel ?? rest['aria-label']}
        options={options}
        value={value}
        defaultValue={options[0]?.value}
        onChange={onChange}
        className={className}
        data-testid={rest['data-testid']}
      />
    )
  }

  const pill = (option: FilterOption<V>, measuring = false) => {
    const active = option.value === value
    const Icon = option.icon
    return (
      <button
        key={option.value}
        type="button"
        tabIndex={measuring ? -1 : tabs ? (active ? 0 : -1) : undefined}
        role={!measuring && tabs ? 'tab' : undefined}
        onClick={measuring ? undefined : () => onChange(option.value)}
        aria-pressed={measuring || tabs ? undefined : active}
        aria-selected={!measuring && tabs ? active : undefined}
        data-value={measuring ? undefined : option.value}
        title={option.hideLabel ? option.label : undefined}
        className={cn(filterPillClass(active), option.hideLabel && Icon && 'px-2.5')}
      >
        {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
        {option.hideLabel && Icon ? <span className="sr-only">{option.label}</span> : option.label}
        {option.count != null && <span className={cn('tabular-nums', active ? 'text-white/80' : 'text-gray-400')}>{option.count}</span>}
        {option.badge && <span className="rounded bg-green-100 px-1.5 py-px text-[11px] font-semibold leading-4 text-green-700">{option.badge}</span>}
      </button>
    )
  }

  const visible = shown ?? new Set(options.map((_, i) => i))
  const hidden = options.filter((_, i) => !visible.has(i))
  const chosenHidden = hidden.find((o) => o.value === value)
  const pick = (v: V) => {
    if (phone) sheet.hide(() => onChange(v))
    else { setMenuOpen(false); onChange(v) }
  }

  const ChosenIcon = chosenHidden?.icon
  const moreButton = (props: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={chosenHidden ? `${t('more')}: ${chosenHidden.label}` : undefined}
      data-testid="filter-pills-more"
      tabIndex={tabs && !chosenHidden ? -1 : undefined}
      className={filterPillClass(Boolean(chosenHidden))}
      {...props}
    >
      {ChosenIcon && <ChosenIcon className="h-3.5 w-3.5" aria-hidden="true" />}
      {chosenHidden ? chosenHidden.label : t('more')}
      <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
    </button>
  )

  return (
    <div className={cn('relative min-w-0', className)} data-testid={rest['data-testid']}>
      {/* Every pill, out of sight, to know their widths; inside a 0×0 clipped box, so it never widens the page. */}
      <div aria-hidden="true" {...{ inert: '' }} className="pointer-events-none invisible absolute left-0 top-0 h-0 w-0 overflow-hidden">
        <div ref={measureRef} className="flex w-max gap-2 whitespace-nowrap">
          {options.map((o) => pill(o, true))}
          <button type="button" tabIndex={-1} className={filterPillClass(false)}>
            {t('more')}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div ref={rowRef} role={tabs ? 'tablist' : 'group'} aria-label={rest['aria-label']} onKeyDown={tabs ? onTabKeyDown : undefined} className="-mx-1 -my-1 flex flex-nowrap gap-2 overflow-hidden px-1 py-1">
        {options.map((o, i) => (visible.has(i) ? pill(o) : null))}
        {hidden.length > 0 && (phone ? (
          moreButton({ onClick: sheet.show })
        ) : (
          <Popover open={menuOpen} onOpenChange={setMenuOpen}>
            <PopoverTrigger asChild>{moreButton({})}</PopoverTrigger>
            <PopoverContent align="end" className="w-60 p-1" aria-label={rest['aria-label']}>
              <FilterOptionList options={hidden} value={value} onPick={pick} autoFocus={false} />
            </PopoverContent>
          </Popover>
        ))}
      </div>

      {phone && hidden.length > 0 && (
        <BottomSheet sheet={sheet} title={rest['aria-label']} data-testid="filter-pills-sheet">
          <div className="px-3 pb-2">
            <FilterOptionList options={hidden} value={value} onPick={pick} />
          </div>
        </BottomSheet>
      )}
    </div>
  )
}
